// @flow
import * as React from 'react';
import {
    Divider,
    Flex,
    Text,
    Stack,
    Columns,
    Column,
    Button,
    Box
} from '@locus-taxy/neo-ui';
import { TimeField } from 'common/components/neo/InlineFields/TimeField';
import SelectField from 'common/components/neo/InlineFields/SelectField';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import { type RescheduleConfig, type SlotShift, getSlotLabel } from './helper';

type Props = {
    rescheduleConfig: RescheduleConfig,
    setConfig: (
        path: string,
        value: number | string | Array<SlotShift>
    ) => void,
    getString: (key: string) => string,
    generateSlots: () => void // not being used
};

const ManualSlotGenerator = ({
    rescheduleConfig,
    setConfig,
    getString
}: Props): React.Node => {
    const [startTime, setStartTime] = React.useState<string>('');
    const [endTime, setEndTime] = React.useState<string>('');
    const [selectedSlotsToDelete, setSelectedSlotsToDelete] = React.useState<
        Array<string>
    >([]);

    const { staticSlots } = rescheduleConfig;

    const handleAddSlot = () => {
        const newSlots = [...staticSlots, { startTime, endTime }];
        setStaticSlots(newSlots);
        setStartTime('');
        setEndTime('');
    };
    const handleSelectSlots = (
        slots: Array<{ key: string, value: string }>
    ) => {
        setSelectedSlotsToDelete(slots.map(slot => slot.value));
    };
    const handleDeleteSlots = () => {
        const filteredStaticSlots = staticSlots.filter(
            slot => !selectedSlotsToDelete.includes(getSlotLabel(slot))
        );

        setStaticSlots(filteredStaticSlots);
        setSelectedSlotsToDelete([]);
    };

    const setStaticSlots = (slots: Array<SlotShift>) => {
        setConfig('staticSlots', slots);
    };

    const slotOptions = staticSlots.map(slot => ({
        value: getSlotLabel(slot),
        label: getSlotLabel(slot)
    }));

    const minSlotEndTime = startTime
        ? TimeUtils.getMoment(startTime, DateTimeFormat.HTML5_FMT_TIME)
        : '';

    const maxSlotStartTime = endTime
        ? TimeUtils.getMoment(endTime, DateTimeFormat.HTML5_FMT_TIME)
        : '';

    return (
        <Stack space="xSmall">
            <Box borderRadius={4} backgroundColor="white" padding={4}>
                <Box mb={2}>
                    <Text fontSize={2} fontWeight="400" mb={2}>
                        {getString('createSlots.createStaticSlots')}
                    </Text>
                    <Divider color="grey.200" />
                    <Text fontSize={2} fontWeight="400" color="grey.600" pt={2}>
                        {getString('createSlots.createStaticSlotsDesc')}
                    </Text>
                </Box>
                <Columns space="medium">
                    <Column>
                        <TimeField
                            label={getString('createSlots.startTime')}
                            required
                            value={startTime}
                            onChange={setStartTime}
                            fullWidth
                            max={maxSlotStartTime}
                        />
                    </Column>
                    <Column>
                        <TimeField
                            label={getString('createSlots.endTime')}
                            required
                            value={endTime}
                            onChange={setEndTime}
                            fullWidth
                            min={minSlotEndTime}
                        />
                    </Column>
                </Columns>
                <Flex justifyContent="flex-end" width="100%" mt={4}>
                    <Button
                        onClick={handleAddSlot}
                        appearance="ghost"
                        palette="primary"
                        icon="add"
                        disabled={!startTime || !endTime}
                    >
                        {getString('createSlots.addSlot')}
                    </Button>
                </Flex>
            </Box>
            <Box backgroundColor="white" borderRadius={4} padding={4} mt={2}>
                <Text fontSize={2} fontWeight="400" mb={2}>
                    {getString('createSlots.deleteSlots')}
                </Text>
                <Divider color="grey.200" />
                <Box mt={4}>
                    <Columns alignY="center" space="medium">
                        <Column width="3/4">
                            <SelectField
                                isMulti
                                closeMenuOnSelect={false}
                                value={selectedSlotsToDelete}
                                label={getString(
                                    'createSlots.selectSlotsToDelete'
                                )}
                                onChange={handleSelectSlots}
                                fullWidth
                                options={slotOptions}
                            />
                        </Column>
                        <Column>
                            <Flex justifyContent="flex-end">
                                <Button
                                    appearance="ghost"
                                    palette="danger"
                                    onClick={handleDeleteSlots}
                                    icon="delete"
                                    disabled={!selectedSlotsToDelete.length}
                                >
                                    {getString(
                                        'createSlots.deleteSlotsBtnLabel'
                                    )}
                                </Button>
                            </Flex>
                        </Column>
                    </Columns>
                </Box>
            </Box>
        </Stack>
    );
};

export default ManualSlotGenerator;
